import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { CalendarAltIcon } from 'cokitchen-icons';
import {
  BaseModal,
  Button,
  Input,
  SideModal,
  Switch,
  ToastMessage
} from '@cokitchen/cokitchen-components';

import { apiService } from 'services/base.service';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DateRange from 'components/date-range';
import FileUploader from 'components/base/file-uploader';
import TimeSelector from 'components/base/time-selector';
import { TimeSelectorType } from 'components/base/time-selector/types';
import {
  changeDateTime,
  formatApiTimeToTimeComponentFormat,
  handleApiError
} from 'utils/functions';

export type UpdateEventType = {
  start_date: string;
  end_date: string;
  event_picture: string | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  updateEventDetails: (id: string, newData: UpdateEventType) => void;
  eventDetails: {
    event_picture: string | null;
    start_date: string;
    end_date: string;
    name?: string;
    id: string;
  };
};
type Range = {
  startDate?: Date;
  endDate?: Date;
  key?: string;
};
type EditEventProps = {
  start_date: string;
  end_date: string;
  event_picture: string | null;
  name: string;
};
const EditEventModal = ({ isOpen, onClose, eventDetails, updateEventDetails }: Props) => {
  const [showDate, setShowDate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState<EditEventProps>({
    name: eventDetails?.name ?? '',
    start_date: '',
    end_date: '',
    event_picture: eventDetails?.event_picture || null
  });
  const start = formatApiTimeToTimeComponentFormat(eventDetails.start_date);
  const end = formatApiTimeToTimeComponentFormat(eventDetails.end_date);
  const [startTime, setStartTime] = useState(start as TimeSelectorType);
  const [endTime, setEndTime] = useState(end as TimeSelectorType);
  const rangeKey = 'selection';

  const [state, setState] = useState<Range>({
    startDate: new Date(),
    endDate: new Date(),
    key: rangeKey
  });

  const [endDate, setEndDate] = useState(false);
  const [hasDateChanged, setHasDateChanged] = useState(false);
  useEffect(() => {
    setState({
      ...state,
      startDate: eventDetails?.start_date ? new Date(eventDetails.start_date) : undefined,
      endDate: eventDetails?.end_date ? new Date(eventDetails.end_date) : undefined
    });

    const isSameDay = eventDetails?.start_date === eventDetails?.end_date;
    setEndDate(!isSameDay);
  }, [eventDetails]);

  useEffect(() => {
    if (!hasDateChanged && eventDetails) {
      setEvent({
        ...event,
        start_date: state.startDate?.toISOString() || '',
        end_date: state.endDate?.toISOString() || ''
      });
    }
  }, [state, eventDetails]);

  const handleSelectDate = () => {
    if (state.startDate && state.endDate) {
      const newStartDate = new Date(state.startDate);
      const newEndDate = new Date(state.endDate);
      setEvent({
        ...event,
        start_date: changeDateTime(newStartDate.toISOString(), startTime),
        end_date: changeDateTime(newEndDate.toISOString(), endTime)
      });
    }
  };

  const { id } = useParams();

  const updateEvent = async () => {
    setLoading(true);
    try {
      const res = await apiService.put('events', {
        data: {
          event_id: id ? id : eventDetails?.id,
          ...event
        }
      });
      if (res?.data?.data) {
        updateEventDetails(eventDetails?.id, event);
      }
      toast(<ToastMessage message='Event updated successfully' type='success' />);
    } catch (error) {
      handleApiError(error, 'Error updating event');
    } finally {
      setLoading(false);
      onClose();
    }
  };
  const title = useMemo(() => {
    const { start_date, end_date } = event;
    if (!start_date) return 'Select date';

    const isSameDate = moment(start_date).isSame(end_date, 'day');
    const format = 'DD/MM/YYYY, h:mm a';

    if (isSameDate) {
      return `${moment(start_date).format(format)} - ${moment(end_date).format('h:mm a')}`;
    } else {
      return `${moment(start_date).format(format)} - ${moment(end_date).format(format)}`;
    }
  }, [event.start_date, event.end_date]);

  return (
    <SideModal
      closeBtnVariant='side'
      bodyClassName='w-[98vw] sm:w-[450px]'
      showTopNavbar
      isOpen={isOpen}
      onRequestClose={onClose}>
      <div className='space-y-5'>
        <p className='text-center font-semibold mb-6'>
          {eventDetails?.name ? `Edit ${eventDetails.name}` : 'Edit Event'} details
        </p>
        <div>
          <p className='mb-1 text-[#2E3031] font-medium'>Event name</p>
          <div>
            <Input
              design='bordered'
              value={event?.name}
              onChange={(e) => {
                setEvent((prev) => ({ ...prev, name: e.target.value }));
              }}
            />
          </div>
        </div>

        <div>
          <p className='mb-1 text-[#2E3031] font-medium'>Event date</p>
          <div
            className='cursor-pointer flex justify-between w-full bg-[#FAFAFA] border-b-2 border-b-[#D5D6D7] h-[52px] mb-6 items-center px-[14px] text-[#2E3031] font-medium'
            onClick={() => setShowDate(true)}>
            <p>{title}</p>
            <CalendarAltIcon />
          </div>
        </div>
        {showDate && (
          <BaseModal
            isOpen={showDate}
            onRequestClose={() => setShowDate(false)}
            title='Select Date'>
            <div className='w-full daterange'>
              {
                <DateRange
                  editableDateInputs={true}
                  onChange={(item) => {
                    setState(item[rangeKey]);
                    setHasDateChanged(true);
                  }}
                  moveRangeOnFirstSelection={false}
                  ranges={endDate ? [state] : [{ ...state, endDate: state.startDate }]}
                  dateDisplayFormat='dd/MM/yyyy'
                  className='range'
                  showDateDisplay={endDate}
                  minDate={new Date()}
                  maxDate={new Date(new Date().getFullYear() + 5, 11, 31)}
                />
              }
              <div className='w-[80%] mx-auto'>
                <p className='mb-1 text-[#2E3031] font-medium mt-2 text-sm'>Time</p>
                <div className='flex items-center gap-2 w-full justify-between rounded pt-3 pb-4'>
                  <TimeSelector
                    value={startTime}
                    onChange={(k) => {
                      setStartTime(k);
                    }}
                  />

                  <span className='text-gray-5 text-xs leading-[19px] mt-2 mb-1'>--</span>
                  <TimeSelector
                    value={endTime}
                    onChange={(k) => {
                      setEndTime(k);
                    }}
                  />
                </div>
              </div>
              <div className='flex justify-between w-full mt-6 items-center button_box'>
                <Switch
                  label='End Date'
                  labelPosition='left'
                  active={endDate}
                  onChange={(checked) => setEndDate(checked)}
                  switchClassName='bg-[#e60a2b] w-[38px] h-[22px]'
                />
                <div className='flex gap-3'>
                  <Button
                    variant='block'
                    color='gray'
                    className='w-[90px] h-[42px] text-sm gray_btn'
                    onClick={() => setShowDate(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant='block'
                    color='primary'
                    className='w-[90px] h-[42px] text-sm'
                    onClick={() => {
                      setShowDate(false);
                      handleSelectDate();
                    }}>
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </BaseModal>
        )}
      </div>

      <div>
        <p className='mb-1 text-[#2E3031] font-medium'>Event Picture</p>

        <FileUploader
          fileUrls={event?.event_picture ? [event?.event_picture] : []}
          setFileUrls={(urls) => {
            setEvent({ ...event, event_picture: urls[0] });
          }}
        />
      </div>
      <Button
        variant='block'
        color='primary'
        className='w-full h-[52px] text-sm mt-6'
        loading={loading}
        onClick={updateEvent}>
        Save
      </Button>
    </SideModal>
  );
};

export default EditEventModal;
